import React, { memo, useMemo } from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { useDataProvider, useRefresh } from 'react-admin';

import { Dialog, Divider } from 'components/common';
import UserInfo from './UserInfo';
import { LabeledValueField } from 'components/fields';
import { getCurrency } from 'pages/users/helpers/balance';
import { required } from 'components/form/validationRules';
import { Checkbox, Input } from 'components/form';
import formatPrice from 'services/helpers/formatPrice';

const useRefundStyles = makeStyles((theme) => ({
  calcRow: {
    paddingTop: theme.spacing(2),
  },

  calcTotal: {
    fontWeight: 'bold',
    fontSize: 20,
  },

  priceContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    '& .MuiTypography-root': {
      fontWeight: 'bold',
    },
  },
}));

const RefundDialog = ({ currentTransaction, onCancel, user, balance, currencyCode }) => {
  const classes = useRefundStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { transactions } = currentTransaction;

  const currency = getCurrency(currencyCode);

  const handleSubmitForm = ({ amount, description, refundFee }) => {
    return dataProvider
      .create('balanceRefund', {
        id: user?.id,
        data: {
          amount: refundFee ? Number(amount) + currentTransaction.details.stripeFee : amount,
          description,
          transactionId: transactions[0]?.id,
          refundFee,
        },
      })
      .then(({ data }) => {
        if (data) {
          onCancel();
          refresh();
        }
      })
      .catch((error) => ({ [FORM_ERROR]: error.message }));
  };

  const maxAmount = useMemo(() => {
    const currentBalance = balance.find((item) => item.currency === currencyCode)?.balance;
    const currentAmount = transactions[0]?.amount.amount;
    return currentBalance < currentAmount ? currentBalance : currentAmount;
  }, [balance, currencyCode, transactions]);

  return (
    <Form
      onSubmit={handleSubmitForm}
      initialValues={{
        amount: maxAmount,
        description: 'Refund from Rollo Team',
        refundFee: false,
      }}
      render={({ handleSubmit, submitting, submitError, values }) => {
        const calc = {
          current: transactions[0]?.amount.amount,
        };
        return (
          <form onSubmit={handleSubmit}>
            <Dialog
              open={!!currentTransaction}
              title="Refund"
              onCancel={onCancel}
              confirmText="Refund"
              onConfirm={handleSubmit}
              submitting={submitting}
              error={submitError}
              cancelText="Back">
              <UserInfo user={user} />
              <Divider />
              <LabeledValueField
                classes={{ root: classes.calcRow }}
                label="Transaction Amount"
                record={calc}
                source="current"
                formatter={currency.format}
              />
              <Input
                name="amount"
                label="Amount to refund"
                inputProps={{
                  type: 'number',
                  inputProps: { min: 0, max: maxAmount },
                }}
                validate={required}
              />
              <Input
                name="description"
                label="Description"
                inputProps={{ type: 'text' }}
                validate={required}
              />
              <Checkbox
                name="refundFee"
                label={`Include fee ($${currentTransaction.details.stripeFee})`}
              />
              <Box className={classes.priceContainer}>
                <Typography>Total amount:</Typography>
                <Typography>
                  {formatPrice(
                    values.refundFee
                      ? Number(values.amount) + currentTransaction.details.stripeFee
                      : values.amount
                  )}
                </Typography>
              </Box>
            </Dialog>
          </form>
        );
      }}
    />
  );
};

export default memo(RefundDialog);
